import React, { useCallback, useEffect, useState } from 'react';
import { RECOMMENDATIONS, FAVOURITES, FEATURES, SUPPORT } from './mock';
type SectionKey = 'heads-up' | 'favourites' | 'spaces' | 'support';
type SectionConfig = {
  order: string[];
  limit: number;
};
type DashboardConfig = Record<SectionKey, SectionConfig>;
const DEFAULT_CONFIG: DashboardConfig = {
  'heads-up': {
    order: RECOMMENDATIONS.map(r => r.id),
    limit: 2
  },
  favourites: {
    order: FAVOURITES.map(f => f.id),
    limit: 3
  },
  spaces: {
    order: FEATURES.map(f => f.id),
    limit: 5
  },
  support: {
    order: SUPPORT.map(s => s.id),
    limit: 2
  }
};
const STORAGE_KEY = 'dhp.dashboardConfig.v1';
// Simple event emitter for cross-component sync
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());
let cachedConfig: DashboardConfig | null = null;
function loadConfig(): DashboardConfig {
  if (cachedConfig) return cachedConfig;
  if (typeof window === 'undefined') return DEFAULT_CONFIG;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as DashboardConfig;
      // Reconcile with current ids (in case mock data changed)
      const reconciled = {
        ...DEFAULT_CONFIG
      };
      for (const key of Object.keys(DEFAULT_CONFIG) as SectionKey[]) {
        if (parsed[key]) {
          const defaultIds = DEFAULT_CONFIG[key].order;
          const savedIds = parsed[key].order;
          // Keep saved ids that still exist, append new ones
          const validSavedIds = savedIds.filter(id => defaultIds.includes(id));
          const newIds = defaultIds.filter(id => !savedIds.includes(id));
          reconciled[key] = {
            order: [...validSavedIds, ...newIds],
            limit: Math.min(Math.max(0, parsed[key].limit), defaultIds.length)
          };
        }
      }
      cachedConfig = reconciled;
      return reconciled;
    }
  } catch (e) {
    console.error('Failed to load dashboard config', e);
  }
  cachedConfig = DEFAULT_CONFIG;
  return DEFAULT_CONFIG;
}
function saveConfig(config: DashboardConfig) {
  cachedConfig = config;
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  }
  emit();
}
export function useDashboardConfig() {
  const [config, setConfig] = useState<DashboardConfig>(loadConfig);
  useEffect(() => {
    const handleSync = () => setConfig(loadConfig());
    listeners.add(handleSync);
    return () => {
      listeners.delete(handleSync);
    };
  }, []);
  const getOrder = useCallback((key: SectionKey) => config[key].order, [config]);
  const getLimit = useCallback((key: SectionKey) => config[key].limit, [config]);
  const setOrder = useCallback((key: SectionKey, newOrder: string[]) => {
    saveConfig({
      ...config,
      [key]: {
        ...config[key],
        order: newOrder
      }
    });
  }, [config]);
  const setLimit = useCallback((key: SectionKey, newLimit: number) => {
    saveConfig({
      ...config,
      [key]: {
        ...config[key],
        limit: newLimit
      }
    });
  }, [config]);
  const resetSection = useCallback((key: SectionKey) => {
    saveConfig({
      ...config,
      [key]: {
        order: [...DEFAULT_CONFIG[key].order],
        limit: DEFAULT_CONFIG[key].limit
      }
    });
  }, [config]);
  return {
    getOrder,
    getLimit,
    setOrder,
    setLimit,
    resetSection
  };
}
export function orderItems<T extends {
  id: string;
}>(items: T[], orderIds: string[]): T[] {
  const itemMap = new Map(items.map(item => [item.id, item]));
  const ordered: T[] = [];
  // Add items in saved order
  for (const id of orderIds) {
    const item = itemMap.get(id);
    if (item) {
      ordered.push(item);
      itemMap.delete(id);
    }
  }
  // Append any remaining items (newly added to mock data)
  for (const item of itemMap.values()) {
    ordered.push(item);
  }
  return ordered;
}